/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {DependencyList, RefObject, useLayoutEffect, useState} from 'react';

// how far past its box text may run before it counts as cut off - browsers
// report the two a pixel apart for text that fits (line-height rounding,
// font metrics), which showed a tooltip on short text ...
const TRUNCATION_TOLERANCE_PX = 1;

/**
 * Determines whether an element's text is cut off - running past its box by
 * more than half a line down, or more than a rounding pixel across.
 *
 * ```typescript
 * import {isElementTruncated} from './useIsTruncated';
 *
 * ...
 *
 * const showTooltip = isElementTruncated(textElement);
 * ```
 *
 * @param element The text's element.
 * @returns       `true` when its text is cut off, otherwise `false`.
 */
export const isElementTruncated = (element: HTMLElement): boolean => {
  // a clamped box that cuts text off hides a whole line at least - large text
  // (e.g. a 20px name on a 22px line) can stand a few pixels past its line
  // box without anything hidden, so the height has to run over by more than
  // half a line ...
  const lineHeight      = parseFloat(globalThis.getComputedStyle?.(element)?.lineHeight);
  const heightTolerance = Number.isFinite(lineHeight)
    ? Math.max(TRUNCATION_TOLERANCE_PX, lineHeight / 2)
    : TRUNCATION_TOLERANCE_PX;
  return element.scrollHeight - element.clientHeight > heightTolerance ||
    element.scrollWidth - element.clientWidth > TRUNCATION_TOLERANCE_PX;
};

/**
 * useIsTruncated measures whether the ref'd element's content is actually
 * clipped (`scrollHeight > clientHeight`), re-measuring whenever `deps`
 * changes - shared by `ClampedTextWithTooltip` and the clamped Asset Name
 * link in `AssetSummaryCardIdentity`, both of which only show a tooltip
 * when the text genuinely overflows.
 *
 * ```typescript
 *
 *   import {useIsTruncated} from '@ion/dls/src/components/assetcard/current';
 *   ...
 *
 *   const ref = useRef<HTMLSpanElement>(null);
 *   const isTruncated = useIsTruncated(ref, [children]);
 *
 * ```
 *
 */
export const useIsTruncated = <T extends HTMLElement>(ref: RefObject<T>, deps: DependencyList): boolean => {
  const [isTruncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    let cleanup;
    if (element) {
      function measure() {
        setTruncated(isElementTruncated(element));
      }
      measure();
      // a card rendered as a virtualized list row (react-window) can mount
      // before its row width is settled to its final value, so a single
      // measurement on mount/deps-change alone sticks with whatever width was
      // available at that moment - a ResizeObserver re-measures whenever this
      // element's actual box size later changes, not just when `deps` does ...
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      cleanup = function() {
        observer.disconnect();
      };
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return isTruncated;
};

export default useIsTruncated;
